// ========================================
// PalavrasCruzadas Component
// ========================================
// Versão: 1.0
// Data: 01 de Novembro de 2025

import { useState, useRef } from 'react';
import './PalavrasCruzadas.css';

interface PalavraCruzada {
  numero: number;
  palavra: string;
  significado: string;
  direcao: 'horizontal' | 'vertical';
  inicio: { linha: number; coluna: number };
  tamanho: number;
}

interface Dica {
  numero: number;
  texto: string;
}

interface PalavrasCruzadasProps {
  grade: (string | null)[][];
  palavras: PalavraCruzada[];
  dicas: {
    horizontais: Dica[];
    verticais: Dica[];
  };
  tema: string;
  dificuldade: number;
  onVerificarRespostas?: (corretas: number, incorretas: number) => void;
}

export default function PalavrasCruzadas({
  grade,
  palavras,
  dicas,
  tema,
  dificuldade,
  onVerificarRespostas
}: PalavrasCruzadasProps) {
  const [respostas, setRespostas] = useState<Map<string, string>>(new Map());
  const [celulaSelecionada, setCelulaSelecionada] = useState<{ linha: number; coluna: number } | null>(null);
  const [palavraAtiva, setPalavraAtiva] = useState<PalavraCruzada | null>(null);
  const [corretas, setCorretas] = useState<Set<string>>(new Set());
  const [erros, setErros] = useState<Set<string>>(new Set());
  const [verificado, setVerificado] = useState(false);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const getCelulaKey = (linha: number, coluna: number) => `${linha}-${coluna}`;

  // Helper: get the expected letter from the grid for a given cell (uppercase)
  const getGradeLetter = (linha: number, coluna: number): string | null => {
    const v = grade?.[linha]?.[coluna];
    if (v === null || v === undefined) return null;
    return String(v).toUpperCase();
  };

  // Helper: normalize a character for comparison (strip diacritics, uppercase)
  const normalizeChar = (ch?: string | null): string => {
    if (!ch) return '';
    return ch
      .toString()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Encontrar palavras que contêm uma célula
  const encontrarPalavrasNaCelula = (linha: number, coluna: number): PalavraCruzada[] => {
    return palavras.filter(palavra => {
      const { inicio, tamanho, direcao } = palavra;
      
      if (direcao === 'horizontal') {
        return linha === inicio.linha && 
               coluna >= inicio.coluna && 
               coluna < inicio.coluna + tamanho;
      } else {
        return coluna === inicio.coluna && 
               linha >= inicio.linha && 
               linha < inicio.linha + tamanho;
      }
    });
  };

  // Click em célula
  const handleCellClick = (linha: number, coluna: number) => {
    const palavrasNaCelula = encontrarPalavrasNaCelula(linha, coluna);
    
    if (palavrasNaCelula.length === 0) return;
    
    // Se já está selecionada, alternar direção
    if (celulaSelecionada?.linha === linha && celulaSelecionada?.coluna === coluna) {
      const outraDirecao = palavraAtiva?.direcao === 'horizontal' ? 'vertical' : 'horizontal';
      const palavraOutraDirecao = palavrasNaCelula.find(p => p.direcao === outraDirecao);
      
      if (palavraOutraDirecao) {
        setPalavraAtiva(palavraOutraDirecao);
      }
    } else {
      // Primeira vez clicando: preferir horizontal
      const horizontal = palavrasNaCelula.find(p => p.direcao === 'horizontal');
      setPalavraAtiva(horizontal || palavrasNaCelula[0]);
    }
    
    setCelulaSelecionada({ linha, coluna });
    
    // Focar input
    const key = getCelulaKey(linha, coluna);
    const input = inputRefs.current.get(key);
    if (input) {
      input.focus();
    }
  };

  // Digitar letra
  const handleInput = (linha: number, coluna: number, valor: string) => {
    // Accept a single Unicode letter; ignore other chars
    const lastChar = valor.slice(-1);
    const isLetter = /\p{L}/u.test(lastChar);
    const letra = isLetter ? lastChar.toLocaleUpperCase('pt-BR') : '';

    if (letra.length > 1) return;

    const key = getCelulaKey(linha, coluna);
    const novasRespostas = new Map(respostas);

    if (letra) {
      novasRespostas.set(key, letra);
      setRespostas(novasRespostas);

      // Mover para próxima célula
      moverParaProximaCelula(linha, coluna);
    } else {
      novasRespostas.delete(key);
      setRespostas(novasRespostas);
    }
  };

  // Navegar para próxima célula
  const moverParaProximaCelula = (linha: number, coluna: number) => {
    if (!palavraAtiva) return;
    
    const { inicio, tamanho, direcao } = palavraAtiva;
    let proximaLinha = linha;
    let proximaColuna = coluna;
    
    if (direcao === 'horizontal') {
      proximaColuna++;
      if (proximaColuna >= inicio.coluna + tamanho) return;
    } else {
      proximaLinha++;
      if (proximaLinha >= inicio.linha + tamanho) return;
    }
    
    setCelulaSelecionada({ linha: proximaLinha, coluna: proximaColuna });
    
    const key = getCelulaKey(proximaLinha, proximaColuna);
    const input = inputRefs.current.get(key);
    if (input) {
      setTimeout(() => input.focus(), 0);
    }
  };

  // Navegar para célula anterior
  const moverParaCelulaAnterior = (linha: number, coluna: number) => {
    if (!palavraAtiva) return;
    
    const { inicio, direcao } = palavraAtiva;
    let anteriorLinha = linha;
    let anteriorColuna = coluna;
    
    if (direcao === 'horizontal') {
      anteriorColuna--;
      if (anteriorColuna < inicio.coluna) return;
    } else {
      anteriorLinha--;
      if (anteriorLinha < inicio.linha) return;
    }
    
    setCelulaSelecionada({ linha: anteriorLinha, coluna: anteriorColuna });
    
    const key = getCelulaKey(anteriorLinha, anteriorColuna);
    const input = inputRefs.current.get(key);
    if (input) {
      setTimeout(() => input.focus(), 0);
    }
  };

  // Tecla pressionada
  const handleKeyDown = (e: React.KeyboardEvent, linha: number, coluna: number) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const key = getCelulaKey(linha, coluna);
      if (respostas.has(key)) {
        const novasRespostas = new Map(respostas);
        novasRespostas.delete(key);
        setRespostas(novasRespostas);
      } else {
        moverParaCelulaAnterior(linha, coluna);
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (coluna < grade[0].length - 1 && grade[linha][coluna + 1] !== null) {
        handleCellClick(linha, coluna + 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (coluna > 0 && grade[linha][coluna - 1] !== null) {
        handleCellClick(linha, coluna - 1);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (linha < grade.length - 1 && grade[linha + 1][coluna] !== null) {
        handleCellClick(linha + 1, coluna);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (linha > 0 && grade[linha - 1][coluna] !== null) {
        handleCellClick(linha - 1, coluna);
      }
    }
  };

  // Verificar respostas
  const verificarTodasRespostas = () => {
    const novasCorretas = new Set<string>();
    const novosErros = new Set<string>();
    
    palavras.forEach(palavra => {
      const { inicio, tamanho, direcao } = palavra;

      for (let i = 0; i < tamanho; i++) {
        const linha = direcao === 'horizontal' ? inicio.linha : inicio.linha + i;
        const coluna = direcao === 'horizontal' ? inicio.coluna + i : inicio.coluna;
        const key = getCelulaKey(linha, coluna);
        const respostaUsuario = respostas.get(key) || '';
        const letraCorreta = getGradeLetter(linha, coluna) || '';

        if (normalizeChar(respostaUsuario) === normalizeChar(letraCorreta)) {
          novasCorretas.add(key);
        } else if (respostaUsuario) {
          novosErros.add(key);
        }
      }
    });
    
    setCorretas(novasCorretas);
    setErros(novosErros);
    setVerificado(true);
    
    if (onVerificarRespostas) {
      onVerificarRespostas(novasCorretas.size, novosErros.size);
    }
  };

  // Revelar palavra
  const revelarPalavra = (numero: number, direcao: 'horizontal' | 'vertical') => {
    console.log('[PALAVRAS-CRUZADAS] Tentando revelar palavra:', { 
      numero, 
      direcao,
      totalPalavras: palavras.length,
      palavrasDisponiveis: palavras.map(p => ({ num: p.numero, dir: p.direcao, termo: p.palavra }))
    });
    
    const palavra = palavras.find(p => p.numero === numero && p.direcao === direcao);
    
    if (!palavra) {
      console.error('[PALAVRAS-CRUZADAS] ❌ Palavra NÃO encontrada!', { 
        buscando: { numero, direcao },
        disponíveis: palavras.map(p => `${p.numero}-${p.direcao}`)
      });
      return;
    }
    
    const novasRespostas = new Map(respostas);
    const { inicio, direcao: dir, tamanho } = palavra;

    console.log('[PALAVRAS-CRUZADAS] ✅ Palavra encontrada:', {
      numero,
      direcao,
      inicio,
      tamanho
    });

    // Revelar exatamente as células da grade ocupadas por esta palavra
    for (let i = 0; i < tamanho; i++) {
      const linha = dir === 'horizontal' ? inicio.linha : inicio.linha + i;
      const coluna = dir === 'horizontal' ? inicio.coluna + i : inicio.coluna;
      const key = getCelulaKey(linha, coluna);
      const letraGrade = getGradeLetter(linha, coluna);
      if (letraGrade) {
        novasRespostas.set(key, letraGrade);
        console.log(`[PALAVRAS-CRUZADAS] Revelando letra ${i + 1}/${tamanho}: ${letraGrade} em [${linha},${coluna}]`);
      } else {
        console.warn('[PALAVRAS-CRUZADAS] ⚠️ Célula sem letra na grade ao revelar', { linha, coluna });
      }
    }

    console.log('[PALAVRAS-CRUZADAS] 🎉 Palavra totalmente revelada (via grade):', {
      tamanho,
      novasRespostasTotal: novasRespostas.size
    });

    setRespostas(novasRespostas);
  };

  // Click em dica
  const handleDicaClick = (numero: number, direcao: 'horizontal' | 'vertical') => {
    const palavra = palavras.find(p => p.numero === numero && p.direcao === direcao);
    
    if (!palavra) return;
    
    setPalavraAtiva(palavra);
    setCelulaSelecionada({ linha: palavra.inicio.linha, coluna: palavra.inicio.coluna });
    
    const key = getCelulaKey(palavra.inicio.linha, palavra.inicio.coluna);
    const input = inputRefs.current.get(key);
    if (input) {
      input.focus();
    }
  };

  // Verificar se palavra está completa
  const palavraCompleta = (palavra: PalavraCruzada): boolean => {
    const { inicio, tamanho, direcao } = palavra;

    for (let i = 0; i < tamanho; i++) {
      const linha = direcao === 'horizontal' ? inicio.linha : inicio.linha + i;
      const coluna = direcao === 'horizontal' ? inicio.coluna + i : inicio.coluna;
      const key = getCelulaKey(linha, coluna);
      const respostaUsuario = respostas.get(key) || '';
      const letraCorreta = getGradeLetter(linha, coluna) || '';

      if (normalizeChar(respostaUsuario) !== normalizeChar(letraCorreta)) {
        return false;
      }
    }

    return true;
  };

  // Verificar se célula pertence à palavra ativa
  const celulaNaPalavraAtiva = (linha: number, coluna: number): boolean => {
    if (!palavraAtiva) return false;
    
    const { inicio, tamanho, direcao } = palavraAtiva;
    
    if (direcao === 'horizontal') {
      return linha === inicio.linha && 
             coluna >= inicio.coluna && 
             coluna < inicio.coluna + tamanho;
    } else {
      return coluna === inicio.coluna && 
             linha >= inicio.linha && 
             linha < inicio.linha + tamanho;
    }
  };

  // Obter número da célula (se ela inicia alguma palavra)
  const obterNumeroCelula = (linha: number, coluna: number): number | null => {
    const palavra = palavras.find(p => p.inicio.linha === linha && p.inicio.coluna === coluna);
    return palavra ? palavra.numero : null;
  };

  const renderDifficultyStars = () => {
    return '★'.repeat(Math.min(dificuldade, 10));
  };

  return (
    <div className="palavrascruzadas-container">
      {/* Cabeçalho */}
      <div className="palavrascruzadas-header">
        <div>
          <h3>{tema}</h3>
          <span className="badge bg-primary">
            {renderDifficultyStars()} {dificuldade}/10
          </span>
        </div>
        <div>
          <button 
            className="btn btn-success btn-lg"
            onClick={verificarTodasRespostas}
            disabled={verificado}
          >
            {verificado ? '✅ Verificado' : '🔍 Verificar Respostas'}
          </button>
        </div>
      </div>

      <div className="row g-3">
        {/* Grade - alinhada à esquerda */}
        <div className="col-lg-7 col-md-12">
          <div className="grade-container-cruzadas">
            <div className="grade-wrapper-cruzadas">
              {grade.map((linha, linhaIdx) => (
                <div key={linhaIdx} className="grade-linha-cruzadas">
                  {linha.map((celula, colunaIdx) => {
                    if (celula === null) {
                      return <div key={colunaIdx} className="celula-cruzadas vazia"></div>;
                    }
                    
                    const key = getCelulaKey(linhaIdx, colunaIdx);
                    const numero = obterNumeroCelula(linhaIdx, colunaIdx);
                    const isAtiva = celulaSelecionada?.linha === linhaIdx && celulaSelecionada?.coluna === colunaIdx;
                    const isPalavraAtiva = celulaNaPalavraAtiva(linhaIdx, colunaIdx);
                    const isCorreta = corretas.has(key);
                    const isErro = erros.has(key);
                    
                    return (
                      <div
                        key={colunaIdx}
                        className={`celula-cruzadas ${isAtiva ? 'ativa' : ''} ${isPalavraAtiva ? 'palavra-ativa' : ''} ${isCorreta ? 'correta' : ''} ${isErro ? 'erro' : ''}`}
                        onClick={() => handleCellClick(linhaIdx, colunaIdx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCellClick(linhaIdx, colunaIdx);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {numero && <span className="celula-numero">{numero}</span>}
                        <input
                          ref={(el) => {
                            if (el) {
                              inputRefs.current.set(key, el);
                            }
                          }}
                          type="text"
                          inputMode="text"
                          autoComplete="off"
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck={false}
                          maxLength={1}
                          value={respostas.get(key) || ''}
                          onChange={(e) => handleInput(linhaIdx, colunaIdx, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, linhaIdx, colunaIdx)}
                          onFocus={() => {
                            if (!celulaSelecionada || celulaSelecionada.linha !== linhaIdx || celulaSelecionada.coluna !== colunaIdx) {
                              handleCellClick(linhaIdx, colunaIdx);
                            }
                          }}
                          disabled={verificado}
                          aria-label={`Célula linha ${linhaIdx + 1}, coluna ${colunaIdx + 1}`}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dicas */}
        <div className="col-lg-5 col-md-12">
          <div className="dicas-painel">
            {/* Horizontais */}
            <div className="dicas-secao mb-4">
              <h5>➡️ Horizontais</h5>
              <ul className="dicas-lista">
                {dicas.horizontais.map((dica, idx) => {
                  const palavra = palavras.find(p => p.numero === dica.numero && p.direcao === 'horizontal');
                  const completa = palavra ? palavraCompleta(palavra) : false;
                  const ativa = palavraAtiva?.numero === dica.numero && palavraAtiva?.direcao === 'horizontal';
                  
                  return (
                    <li
                      key={`horizontal-${dica.numero}-${idx}`}
                      className={`dica-item ${completa ? 'completa' : ''} ${ativa ? 'ativa' : ''}`}
                      onClick={() => handleDicaClick(dica.numero, 'horizontal')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDicaClick(dica.numero, 'horizontal');
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="dica-numero">{dica.numero}</span>
                      <span className="dica-texto">{dica.texto}</span>
                      {!verificado && (
                        <button
                          className="btn btn-sm btn-outline-secondary btn-revelar"
                          onClick={(e) => {
                            e.stopPropagation();
                            revelarPalavra(dica.numero, 'horizontal');
                          }}
                        >
                          💡
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Verticais */}
            <div className="dicas-secao">
              <h5>⬇️ Verticais</h5>
              <ul className="dicas-lista">
                {dicas.verticais.map((dica, idx) => {
                  const palavra = palavras.find(p => p.numero === dica.numero && p.direcao === 'vertical');
                  const completa = palavra ? palavraCompleta(palavra) : false;
                  const ativa = palavraAtiva?.numero === dica.numero && palavraAtiva?.direcao === 'vertical';
                  
                  return (
                    <li
                      key={`vertical-${dica.numero}-${idx}`}
                      className={`dica-item ${completa ? 'completa' : ''} ${ativa ? 'ativa' : ''}`}
                      onClick={() => handleDicaClick(dica.numero, 'vertical')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDicaClick(dica.numero, 'vertical');
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="dica-numero">{dica.numero}</span>
                      <span className="dica-texto">{dica.texto}</span>
                      {!verificado && (
                        <button
                          className="btn btn-sm btn-outline-secondary btn-revelar"
                          onClick={(e) => {
                            e.stopPropagation();
                            revelarPalavra(dica.numero, 'vertical');
                          }}
                        >
                          💡
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
